import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";

const NAV = [
  { to: "/", label: "Início" },
  { to: "/sobre", label: "Sobre" },
  { to: "/solucoes", label: "Soluções" },
  { to: "/blog", label: "Blog" },
  { to: "/contato", label: "Contato" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 bg-[#050B18]/80 backdrop-blur-xl transition-[box-shadow,border-color] duration-200 ${
        scrolled
          ? "border-b border-gray-200 shadow-[0_1px_0_rgba(0,0,0,0.02)]"
          : "border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Logo />

        <nav
          data-testid="desktop-nav"
          className="hidden lg:flex items-center gap-1"
        >
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-link-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "text-[#1E3A8A]"
                    : "text-gray-700 hover:text-[#111827]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  <span
                    className={`absolute left-3 right-3 -bottom-[1px] h-[2px] bg-[#1E3A8A] transition-transform origin-left ${
                      isActive ? "scale-x-100" : "scale-x-0"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/contato"
            data-testid="nav-cta-especialista"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] transition-colors"
          >
            Fale com um especialista
            <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
          </Link>
        </div>

        <button
          type="button"
          data-testid="mobile-nav-toggle"
          aria-label={open ? "Fechar menu" : "Abrir menu"}
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md hover:bg-gray-100 transition-colors"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-nav-panel"
          className="lg:hidden border-t border-gray-200 bg-[#050B18]"
        >
          <nav className="px-6 py-4 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-3 text-base font-medium border-l-2 transition-colors ${
                    isActive
                      ? "border-[#1E3A8A] text-[#1E3A8A] bg-gray-50"
                      : "border-transparent text-gray-800 hover:bg-gray-50"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contato"
              data-testid="mobile-nav-cta"
              className="mt-3 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] transition-colors"
            >
              Fale com um especialista
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
